import React, { useState } from 'react';
import { Eye, EyeOff, ChevronLeft, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// QR Code Image Import - Ye aapke src/assets/qr.jpeg ko sahi se load karega
import qrImage from '../../assets/qr.jpeg';

const ViewAccount = ({ userData }: { userData?: any }) => {
  const navigate = useNavigate();
  const [showAccountInfo, setShowAccountInfo] = useState(false);
  const [activeTab, setActiveTab] = useState<'Summary' | 'Transactions'>('Summary');
  
  // Modal toggle state for Add Money
  const [showQrModal, setShowQrModal] = useState(false);

  if (!userData) return null;

  // Format values
  const balance = userData.wallet_balance ? parseFloat(userData.wallet_balance).toFixed(2) : '0.00';
  const maskedAccount = userData.account_number 
    ? 'X'.repeat(Math.max(0, userData.account_number.length - 4)) + userData.account_number.slice(-4) 
    : 'XXXXXXXXXXXX';

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-10 relative">
      
      {/* Header Back Button */}
      <button onClick={() => navigate('/account/dashboard')} className="flex items-center gap-2 text-white hover:text-gray-300 transition-colors font-bold text-lg mb-2">
        <ChevronLeft size={24} /> Accounts
      </button>

      {/* Main Account Card */}
      <div className="relative overflow-hidden rounded-[32px] bg-gradient-to-br from-[#731585] via-[#5c116b] to-[#3d0a47] p-8 shadow-2xl">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-2xl -translate-y-1/2 translate-x-1/4"></div>
        <div className="absolute bottom-0 right-10 w-40 h-40 bg-purple-500/20 rounded-full blur-xl translate-y-1/4"></div>

        <div className="relative z-10 flex justify-between items-start mb-6">
          <div>
            <p className="text-white/80 font-bold text-xs uppercase tracking-wider mb-1">Account Number</p>
            <div className="flex items-center gap-3">
              <p className="text-2xl font-bold text-white tracking-widest">
                {showAccountInfo ? userData.account_number : maskedAccount}
              </p>
              <button onClick={() => setShowAccountInfo(!showAccountInfo)} className="text-white/80 hover:text-white transition-colors">
                {showAccountInfo ? <EyeOff size={22} /> : <Eye size={22} />}
              </button>
            </div>
          </div>
          <p className="text-white/90 font-bold text-xs uppercase tracking-wider">Savings A/C</p>
        </div>

        <div className="relative z-10 mb-8">
          <p className="text-white/80 font-bold text-xs uppercase tracking-wider mb-1">Available Balance</p>
          <h2 className="text-4xl md:text-5xl font-bold text-white">
            ₹{balance}
          </h2>
        </div>

        {/* Buttons Section */}
        <div className="relative z-10 flex gap-4 mt-2">
          <button className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold py-3 rounded-2xl border border-white/20 transition-colors text-sm">
            Manage Account
          </button>
          <button 
            onClick={() => setShowQrModal(true)} 
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-2xl border border-blue-500/50 transition-colors text-sm shadow-lg shadow-blue-900/30"
          >
            Add Money
          </button>
        </div>
      </div>

      {/* Details Section */}
      <div className="bg-[#131b2c] rounded-[32px] overflow-hidden shadow-xl border border-gray-800">
        
        {/* Tabs */}
        <div className="flex bg-[#1e222d] p-2 m-4 rounded-2xl">
          <button 
            onClick={() => setActiveTab('Summary')}
            className={`flex-1 py-3 text-sm font-bold rounded-xl transition-all ${activeTab === 'Summary' ? 'bg-[#131b2c] text-white shadow-md' : 'text-gray-400 hover:text-gray-200'}`}
          >
            Summary
          </button>
          <button 
            onClick={() => setActiveTab('Transactions')}
            className={`flex-1 py-3 text-sm font-bold rounded-xl transition-all ${activeTab === 'Transactions' ? 'bg-[#131b2c] text-white shadow-md' : 'text-gray-400 hover:text-gray-200'}`}
          >
            Transactions
          </button>
        </div>

        {activeTab === 'Summary' && (
          <div className="p-6 md:p-8 space-y-8">
            <div className="space-y-4">
              <div className="flex justify-between items-center text-sm md:text-base">
                <span className="text-gray-300">Available Balance</span>
                <span className="text-white font-bold">₹{balance}</span>
              </div>
              <div className="flex justify-between items-center text-sm md:text-base">
                <span className="text-gray-400">Hold/Lien Amount</span>
                <span className="text-gray-400 font-medium">₹0.00</span>
              </div>
              <div className="flex justify-between items-center text-sm md:text-base">
                <span className="text-gray-400">Uncleared Balance</span>
                <span className="text-gray-400 font-medium">₹0.00</span>
              </div>
              <div className="flex justify-between items-center text-sm md:text-base">
                <span className="text-gray-400">MOD Balance</span>
                <span className="text-gray-400 font-medium">₹0.00</span>
              </div>
            </div>

            <div className="pt-6 border-t border-dashed border-gray-700">
              <p className="text-xs text-gray-500 mb-1">Account Description</p>
              <p className="text-blue-400 font-bold uppercase tracking-wide text-sm mb-6">LOTUS SAVING BANK-ADHAR- CHQ</p>
              
              <div className="grid grid-cols-2 gap-y-6">
                <div className="border-b border-dashed border-gray-700 pb-4">
                  <p className="text-xs text-gray-500 mb-1">Currency</p>
                  <p className="text-white font-bold text-sm">Rupees</p>
                </div>
                <div className="border-b border-dashed border-gray-700 pb-4">
                  <p className="text-xs text-gray-500 mb-1">Mode of Operation</p>
                  <p className="text-white font-bold text-sm">Single</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Rate of Interest</p>
                  <p className="text-white font-bold text-sm">2.50%</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Nominee(s)</p>
                  <button className="text-blue-500 hover:text-blue-400 font-bold text-sm border-b border-blue-500">View Details</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'Transactions' && (
          <div className="p-8 text-center text-gray-400">
            <p className="mb-4">Switch to the main Transactions tab for detailed ledger view.</p>
            <button onClick={() => navigate('/account/transactions')} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition-colors">Go to Transactions</button>
          </div>
        )}
      </div>

      {/* ADD MONEY QR MODAL */}
      {showQrModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#050b14]/90 backdrop-blur-sm p-4">
          <div className="bg-[#131b2c] border border-gray-800 rounded-[32px] p-8 max-w-sm w-full shadow-2xl relative text-center">
            
            {/* Close Button */}
            <button 
              onClick={() => setShowQrModal(false)} 
              className="absolute top-4 right-4 text-gray-500 hover:text-white bg-[#1e222d] p-2 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
            
            <h3 className="text-xl font-bold text-white mb-6">Add Funds via UPI</h3>
            
            {/* QR Code Container Using the Imported Image */}
            <div className="bg-white p-3 rounded-2xl inline-block mb-6 shadow-lg shadow-blue-900/20">
              <img 
                src={qrImage} 
                alt="Scan to Pay" 
                className="w-48 h-48 object-cover rounded-xl"
              />
            </div>
            
            {/* Instructions */}
            <p className="text-gray-400 text-sm leading-relaxed mb-6 px-2">
              Please scan QR and via UPI apps like GPay, PhonePe, etc., and send the transaction screenshot on WhatsApp:
            </p>
            
            {/* WhatsApp Number */}
            <div className="bg-green-500/10 border border-green-500/20 rounded-xl py-3 px-4 inline-block">
              <p className="text-green-500 font-bold tracking-widest">+91 1234567890</p>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default ViewAccount;